import React, { useState } from "react";
import moment from "moment";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { Fatura } from "../../../types/Assinatura";
import { ViewListaFaturas } from "../styles";

interface ListaFaturasProps {
  faturas: Fatura[];
}

const LIMITE = 10;

const formatarValor = (v: string | number) => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
};

const formatarValorComParcelas = (valorCobrado: string | number, parcelas: number) => {
  const valor = typeof valorCobrado === "string" ? parseFloat(valorCobrado) : valorCobrado;
  if (parcelas > 1) {
    return `${parcelas}x de R$ ${formatarValor(valor / parcelas)}`;
  }
  return `R$ ${formatarValor(valor)}`;
};

// Valor da parcela na sub-linha: "N/total de R$ x". Derivado da fatura-pai.
const formatarValorParcela = (
  valorCobrado: string | number,
  parcelas: number,
  parcela: number
) => {
  const valor = typeof valorCobrado === "string" ? parseFloat(valorCobrado) : valorCobrado;
  return `${parcela}/${parcelas} de R$ ${formatarValor(valor / parcelas)}`;
};

const formatarData = (d: string | null) =>
  d ? moment(d, "YYYY-MM-DD").format("DD/MM/YYYY") : "-";

// Vencimento "fake" da parcela: venc. do ciclo + (N-1) meses. Todas já pagas,
// serve só de referência visual (não vem do back — é derivável).
const formatarDataParcela = (dataCiclo: string, parcela: number) =>
  moment(dataCiclo, "YYYY-MM-DD").add(parcela - 1, "months").format("DD/MM/YYYY");

/** Ciclo encerrado por upgrade foi pago — exibe como "Pago" pra não confundir. */
const statusVisual = (status: Fatura["status"]): Fatura["status"] =>
  status === "cancelado_upgrade" ? "pago" : status;

const statusLabel = (status: Fatura["status"]) => {
  switch (status) {
    case "pago":
      return "Pago";
    case "aguardando":
      return "Aguardando";
    case "vencido":
      return "Vencido";
    case "cancelado":
      return "Cancelado";
    default:
      return status;
  }
};

const formaLabel = (forma: Fatura["forma_pagamento"]) => {
  switch (forma) {
    case "cartao":
      return "Cartão";
    case "pix":
      return "PIX";
    case "boleto":
      return "Boleto";
    default:
      return forma;
  }
};

const ChevronBaixo: React.FC<{ aberto: boolean }> = ({ aberto }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transition: "transform 0.2s", transform: aberto ? "rotate(180deg)" : "none" }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const ListaFaturas: React.FC<ListaFaturasProps> = ({ faturas }) => {
  const { theme } = useTheme();
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());

  const alternar = (id: number) =>
    setExpandidos((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  if (!faturas.length) {
    return (
      <ViewListaFaturas theme={theme}>
        <div className="vazio">Você ainda não possui faturas geradas.</div>
      </ViewListaFaturas>
    );
  }

  const faturasExibidas = faturas.slice(0, LIMITE);

  // Zebra contínua: alterna por linha VISÍVEL (fatura + parcelas expandidas).
  // 1ª linha branca (base); faixa nas ímpares — igual ao Figma (#2 branca).
  const linhas: React.ReactNode[] = [];
  let visualIdx = 0;
  const zebra = () => (visualIdx++ % 2 === 1 ? " zebra" : "");

  faturasExibidas.forEach((f) => {
    const sv = statusVisual(f.status);
    const nfs = f.notas_fiscais ?? [];
    const parcelado = f.parcelas > 1;
    const temNota = nfs.length > 0;
    const aberto = expandidos.has(f.id);

    linhas.push(
      <div key={`f-${f.id}`} className={`linha${zebra()}`}>
        <span className="col-num">
          {/* Slot fixo da seta: mantém os números alinhados com/sem chevron. */}
          <span className="chevron-slot">
            {parcelado && temNota && (
              <button
                type="button"
                className="btn-chevron"
                onClick={() => alternar(f.id)}
                aria-expanded={aberto}
                aria-label={aberto ? "Recolher notas" : "Ver notas das parcelas"}
              >
                <ChevronBaixo aberto={aberto} />
              </button>
            )}
          </span>
          #{f.numero_ciclo}
        </span>
        <span>{formatarData(f.data_vencimento)}</span>
        <span>{formatarValorComParcelas(f.valor_cobrado, f.parcelas)}</span>
        <span>{formaLabel(f.forma_pagamento)}</span>
        <span className={`status-badge status-${sv}`}>{statusLabel(sv)}</span>
        <span className="col-nota">
          {!temNota ? null : parcelado ? (
            // Parcelado: "Baixar" da linha-mãe só expande (igual ao chevron).
            <button type="button" className="btn-baixar" onClick={() => alternar(f.id)}>
              Baixar
            </button>
          ) : (
            <a className="btn-baixar" href={nfs[0].url}>
              Baixar
            </a>
          )}
        </span>
      </div>
    );

    if (parcelado && aberto && temNota) {
      nfs.forEach((nf) => {
        linhas.push(
          <div key={`f-${f.id}-p-${nf.parcela_numero}`} className={`linha linha-parcela${zebra()}`}>
            <span className="col-num">
              <span className="chevron-slot" />
            </span>
            <span>{formatarDataParcela(f.data_vencimento, nf.parcela_numero)}</span>
            <span>{formatarValorParcela(f.valor_cobrado, f.parcelas, nf.parcela_numero)}</span>
            <span>{formaLabel(f.forma_pagamento)}</span>
            <span className="status-badge status-pago">Pago</span>
            <span className="col-nota">
              <a className="btn-baixar" href={nf.url}>
                Baixar
              </a>
            </span>
          </div>
        );
      });
    }
  });

  return (
    <ViewListaFaturas theme={theme}>
      <div className="tabela">
        <div className="cabecalho">
          <span>Nº</span>
          <span>Vencimento</span>
          <span>Valor</span>
          <span>Método</span>
          <span>Status</span>
          <span>Nota fiscal</span>
        </div>
        {linhas}
      </div>
    </ViewListaFaturas>
  );
};

export default ListaFaturas;
